export type Bucket = "hourly" | "daily" | "weekly" | "monthly";

export const BUCKET_PG: Record<Bucket, "hour" | "day" | "week" | "month"> = {
  hourly:  "hour",
  daily:   "day",
  weekly:  "week",
  monthly: "month",
};

export function toISODate(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function addDays(d: Date, days: number): Date {
  const next = new Date(d);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

export function startOfDayUtc(d: Date): Date {
  const next = new Date(d);
  next.setUTCHours(0, 0, 0, 0);
  return next;
}

const USD_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

const USD_FRACTIONAL_FORMATTER = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 4,
  maximumFractionDigits: 6,
});

export function fmtUsd(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "$0.00";
  if (Math.abs(n) < 1) return USD_FRACTIONAL_FORMATTER.format(n);
  return USD_FORMATTER.format(n);
}

export function fmtUsdCompact(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "$0";
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 1e6) return `${sign}$${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${sign}$${(abs / 1e3).toFixed(1)}k`;
  if (abs < 1) return USD_FRACTIONAL_FORMATTER.format(n);
  return USD_FORMATTER.format(n);
}

export function fmtTokens(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "0";
  const abs = Math.abs(n);
  if (abs >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (abs >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return Math.round(n).toLocaleString();
}

export function fmtCount(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "0";
  return Math.round(n).toLocaleString();
}

export function fmtMs(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "-";
  if (n < 1000) return `${Math.round(n)} ms`;
  return `${(n / 1000).toFixed(2)} s`;
}

export function fmtPctOfTotal(part: number, total: number): string {
  if (!total || !Number.isFinite(total)) return "0%";
  return `${((part / total) * 100).toFixed(1)}%`;
}

// SQL groups rows at the user's chosen bucket granularity, so the JS layer
// just needs to render the labels — no second-pass bucketing required.
export function bucketLabel(iso: string, bucket: Bucket): string {
  const d = new Date(iso);
  const month = d.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
  if (bucket === "monthly") {
    return `${month} ${d.getUTCFullYear()}`;
  }
  if (bucket === "hourly") {
    const hh = String(d.getUTCHours()).padStart(2, "0");
    return `${month} ${d.getUTCDate()}, ${hh}:00`;
  }
  return `${month} ${d.getUTCDate()}`;
}

const FULL_ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

// Convert any timestamp-ish string into a full ISO timestamp. Lets us accept
// the legacy YYYY-MM-DD format from saved filter state without crashing.
export function toIsoTimestamp(value: string): string {
  if (FULL_ISO_RE.test(value)) {
    // Already a full ISO timestamp — normalise via Date round-trip.
    return new Date(value).toISOString();
  }
  // Treat bare YYYY-MM-DD as start-of-day UTC.
  return new Date(`${value}T00:00:00Z`).toISOString();
}

// Format a Date as the value expected by <input type="datetime-local">.
// That input wants local time without a timezone suffix.
export function isoToDatetimeLocal(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function datetimeLocalToIso(local: string): string {
  // datetime-local emits "YYYY-MM-DDTHH:MM" — interpret as local time.
  return new Date(local).toISOString();
}
